import axios from "api/axios"
import { Button } from "components/button"
import { SFlatContent, SFlatHeader } from "components/segment/flat"
import TableComponent, { useTable } from "components/table"
import * as React from "react"
import { RiAddFill } from "react-icons/ri"
import { useQuery } from "react-query"
import { useHistory } from "react-router-dom"
import { productionType } from "dictionary/production_type"
import { useTranslation } from "react-i18next"

export default function Divisions() {
  const history = useHistory()
  const { t } = useTranslation()
  const tBase = "views.admin.categories.index"
  const tr = (key: string) => t(`${tBase}.${key}`)

  type TDivisionListRow = {
    id: string
    label: string
    production?: string
    custom_fields?: { id: string }[]
  }

  type TDivisionTableRow = TDivisionListRow & {
    custom_fields_count: number
    type_label: string
  }

  const table = useTable({
    defaultSort: {
      column: "id",
      order: "DESC",
    },
  })

  const divisions = useQuery<TDivisionListRow[]>(
    "divisions",
    async () =>
      await axios
        .get(
          `${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/divisions`
        )
        .then((res) => res.data)
  )

  const data = React.useMemo<TDivisionTableRow[]>(() => {
    const rows =
      divisions.data?.map((division) => ({
        ...division,
        custom_fields_count: division.custom_fields?.length || 0,
        type_label:
          productionType.find((type) => type.value === division.production)?.label ||
          "",
      })) || []

    const sortedRows = [...rows]
    const sortColumn = table.sort?.column as keyof TDivisionTableRow | null
    const sortOrder = table.sort?.order

    if (!sortColumn || !sortOrder) return sortedRows

    sortedRows.sort((a, b) => {
      const aValue = a[sortColumn]
      const bValue = b[sortColumn]

      if (sortColumn === "id") {
        const aId = Number(aValue)
        const bId = Number(bValue)
        return sortOrder === "ASC" ? aId - bId : bId - aId
      }

      if (sortColumn === "custom_fields_count") {
        const aCount = Number(aValue)
        const bCount = Number(bValue)
        return sortOrder === "ASC" ? aCount - bCount : bCount - aCount
      }

      const aString = String(aValue || "").toLowerCase()
      const bString = String(bValue || "").toLowerCase()

      if (aString === bString) return 0
      if (sortOrder === "ASC") return aString > bString ? 1 : -1
      return aString < bString ? 1 : -1
    })

    return sortedRows
  }, [divisions.data, table.sort?.column, table.sort?.order])

  const columns = {
    id: {
      label: "ID",
    },
    label: {
      label: tr("Label"),
    },
    custom_fields_count: {
      label: tr("Custom Fields"),
    },
    type_label: {
      label: tr("Production Based On"),
    },
  }

  return (
    <div>
      <SFlatHeader>
        <h1>{tr("Divisions")}</h1>
        <Button primary onClick={() => history.push("/admin/divisions/new")}>
          <RiAddFill /> {tr("Divisions")}
        </Button>
      </SFlatHeader>
      <SFlatContent style={{ display: "flex", flexDirection: "column" }}>
        <TableComponent
          data={data}
          columns={columns}
          sort={table.sort}
          handleSort={table.handleSort}
          to={(row) => `/admin/divisions/${row.id}`}
        />
      </SFlatContent>
    </div>
  )
}
